import { PREFIX } from "../../../common/Config";
import { checkProductPromotion } from "../../../../data/epos";
import { resolveSlabIdFromEntity } from "../../../../data/promotions";
import { buildChangedGetProductsForCheckPromotion, isCartLineOfferDisplayRole } from "../../Promotion/utils/promotionOfferProductsShared";
import { isFullLineItemDiscount, resolveProductTaxCode } from "../../Epos/utils/cartCalculations";
import { PROMOTION_MESSAGE_TRIGGERS } from "../../../../utils/promotionMessageToast";

export const applyPromotionCheckTrigger = (
  payload,
  trigger = null,
  changedProductId = null
) => {
  if (!payload || typeof payload !== "object") {
    return payload;
  }
  if (
    trigger === PROMOTION_MESSAGE_TRIGGERS.QTY_CHANGE ||
    trigger === PROMOTION_MESSAGE_TRIGGERS.ADD_PRODUCT
  ) {
    payload.trigger = trigger;
    const id = parseInt(changedProductId, 10);
    if (Number.isFinite(id) && id > 0) {
      payload.changed_product_id = id;
    } else {
      delete payload.changed_product_id;
    }
    return payload;
  }
  if (
    trigger === PROMOTION_MESSAGE_TRIGGERS.PAGE_LOAD ||
    trigger === PROMOTION_MESSAGE_TRIGGERS.APPLY_PROMOTION
  ) {
    payload.trigger = trigger;
    delete payload.changed_product_id;
    return payload;
  }
  delete payload.trigger;
  delete payload.changed_product_id;
  return payload;
};

export const resolvePromotionCheckQueueMeta = (queue) => {
  let best = { trigger: null, changedProductId: null, t: 0 };
  Object.entries(queue || {}).forEach(([pid, meta]) => {
    const entry =
      meta && typeof meta === "object" && meta.t != null
        ? meta
        : { t: Number(meta) || 0, trigger: null };
    const trig = entry.trigger || null;
    if (trig === PROMOTION_MESSAGE_TRIGGERS.ADD_PRODUCT) {
      if (
        best.trigger !== PROMOTION_MESSAGE_TRIGGERS.ADD_PRODUCT ||
        entry.t >= best.t
      ) {
        best = {
          trigger: PROMOTION_MESSAGE_TRIGGERS.ADD_PRODUCT,
          changedProductId: pid,
          t: entry.t,
        };
      }
      return;
    }
    if (
      trig === PROMOTION_MESSAGE_TRIGGERS.QTY_CHANGE &&
      best.trigger !== PROMOTION_MESSAGE_TRIGGERS.ADD_PRODUCT
    ) {
      if (
        best.trigger !== PROMOTION_MESSAGE_TRIGGERS.QTY_CHANGE ||
        entry.t >= best.t
      ) {
        best = {
          trigger: PROMOTION_MESSAGE_TRIGGERS.QTY_CHANGE,
          changedProductId: pid,
          t: entry.t,
        };
      }
    }
  });
  return best;
};

export const transformCartToPromotionPayload = (cartListData, customerId, orderDate, channel = "pos", orderTime = null, orderId = null) => {
    if (!cartListData?.data || !Array.isArray(cartListData.data)) {
        const payload = {
            customer_id: customerId,
            channel: channel,
            order_date: orderDate,
            ...(orderId != null && { order_id: orderId }),
            items: []
        };
        if (orderTime) {
            payload.order_time = orderTime;
        }
        return payload;
    }

    const itemsMap = new Map();

    const normalizePieceType = (val) => {
        const raw = (val || "Box").trim();
        return raw.charAt(0).toUpperCase() + raw.slice(1).toLowerCase();
    };

    cartListData.data.forEach((cartItem) => {
        const productId = parseInt(cartItem.product_id);
        const pieceType = normalizePieceType(cartItem.order_type || cartItem.type);
        const isPromoted = cartItem.is_promoted === "1" || cartItem.is_promoted === 1;
        const promosId = cartItem.promosId || cartItem.promoId || 0;
        const buyOrGetUpper = cartItem.buyOrGet ? String(cartItem.buyOrGet).toUpperCase() : null;
        const promoTypeForKey = cartItem.promo_type || (buyOrGetUpper === "BUY" ? "BUY" : buyOrGetUpper === "GET" ? "GET" : buyOrGetUpper === "FREE" ? "FREE" : "NONE");
        const isGeneratedLine = cartItem.generated === true || cartItem.generated === "1" || cartItem.generated === 1;
        const key = `${productId}_${pieceType}_${isPromoted ? "1" : "0"}_${promosId}_${promoTypeForKey}_${isGeneratedLine ? "gen" : "std"}`;

        const quantity = cartItem.allot_qty !== undefined && cartItem.allot_qty !== null && cartItem.allot_qty !== ""
            ? parseFloat(cartItem.allot_qty) || 0
            : parseFloat(cartItem.qty) || 0;
        const originalOrderQtyRaw = cartItem.original_order_qty !== undefined && cartItem.original_order_qty !== null && cartItem.original_order_qty !== ""
            ? parseFloat(cartItem.original_order_qty)
            : NaN;
        const orderQty = Number.isFinite(originalOrderQtyRaw)
            ? originalOrderQtyRaw
            : (cartItem.order_qty !== undefined && cartItem.order_qty !== null && cartItem.order_qty !== ""
                ? parseFloat(cartItem.order_qty)
                : null);

        if (itemsMap.has(key)) {
            const existing = itemsMap.get(key);
            existing.quantity += quantity;
            if (orderQty !== null && Number.isFinite(orderQty) && existing.order_qty === undefined) {
                existing.order_qty = orderQty;
            }
            if (!existing.comment) {
                const mergedComment = String(
                    cartItem.comment ||
                    cartItem.discount_comment ||
                    cartItem.manualOverrides?.discount_comment ||
                    ""
                ).trim();
                if (mergedComment) {
                    existing.comment = mergedComment;
                }
            }
            const cartPromoType = cartItem.promo_type || (buyOrGetUpper === "BUY" ? "BUY" : buyOrGetUpper === "GET" ? "GET" : buyOrGetUpper === "FREE" ? "FREE" : null);
            if (cartPromoType !== null && cartPromoType !== undefined && (cartPromoType === "BUY" || cartPromoType === "GET" || cartPromoType === "FREE") && existing.promo_type === undefined) {
                existing.promo_type = cartPromoType;
            }
        } else {
            const unitPrice = parseFloat(cartItem.unit_price ?? 0);
            const isContractPriceApplied = cartItem.isContractPriceApplied === "Y" || cartItem.is_contract_price_applied === "Y";
            const itemDiscountValue = cartItem.item_discount !== undefined && cartItem.item_discount !== null && cartItem.item_discount !== ""
                ? parseFloat(cartItem.item_discount)
                : null;
            const hasExplicitCartDiscount = cartItem.discount !== undefined && cartItem.discount !== null && cartItem.discount !== "";
            const discountValue = hasExplicitCartDiscount ? parseFloat(cartItem.discount) : null;
            const manualDiscountRaw = cartItem.manualOverrides?.discount;
            const hasManualDiscountOverride = manualDiscountRaw !== undefined && manualDiscountRaw !== null && String(manualDiscountRaw).trim() !== "";

            let itemDiscountType = cartItem.discount_type || cartItem.productWiseDiscountType || "amount";
            if (hasManualDiscountOverride && cartItem.manualOverrides?.discount_type) {
                itemDiscountType = cartItem.manualOverrides.discount_type;
            }
            const normalizedDiscountType = String(itemDiscountType).trim().toLowerCase();
            const discountTypeForAPI = (normalizedDiscountType === "fixed" || normalizedDiscountType === "£" || normalizedDiscountType === "amount") ? "amount" : "percentage";

            let itemDiscount = 0;
            if (hasManualDiscountOverride) {
                itemDiscount = parseFloat(manualDiscountRaw) || 0;
            } else if (hasExplicitCartDiscount && Number.isFinite(discountValue)) {
                itemDiscount = discountValue;
            } else if (itemDiscountValue !== null && itemDiscountValue > 0) {
                itemDiscount = itemDiscountValue;
            }

            const promoType = cartItem.promo_type || (buyOrGetUpper === "BUY" ? "BUY" : buyOrGetUpper === "GET" ? "GET" : buyOrGetUpper === "FREE" ? "FREE" : null);

            const origPrice = parseFloat(cartItem.original_price ?? (pieceType === "Box" ? cartItem.sales_box_price : cartItem.sales_piece_price) ?? 0);
            const itemPayload = {
                product_id: productId,
                piece_type: pieceType,
                quantity: quantity,
                unit_price: Number.isFinite(unitPrice) ? unitPrice : 0,
                original_price: Number.isFinite(origPrice) ? origPrice : 0,
                promosId: Number.isFinite(Number(promosId)) ? Number(promosId) : 0,
                is_contra: isContractPriceApplied ? "Y" : "N",
                item_discount_type: discountTypeForAPI
            };
            const effectiveDiscount = itemDiscount;
            const isManualDiscount = hasManualDiscountOverride || hasExplicitCartDiscount;
            const hasPromotionApplied = cartItem.promotion_applied === true || promoType !== null || promosId > 0;
            const isPromo100Percent = !isManualDiscount && hasPromotionApplied && discountTypeForAPI === "percentage" && (effectiveDiscount === 100 || effectiveDiscount === 100.0);
            if (isPromo100Percent) {
                itemPayload.item_discount = "0";
            } else {
                itemPayload.item_discount = discountTypeForAPI === "percentage" ? String(effectiveDiscount) : String(itemDiscount);
            }
            if (orderQty !== null && Number.isFinite(orderQty)) {
                itemPayload.order_qty = orderQty;
            }
            if (promoType !== null && promoType !== undefined && (promoType === "BUY" || promoType === "GET" || promoType === "FREE")) {
                itemPayload.promo_type = promoType;
            }
            const catName = cartItem.category_name ?? cartItem.categorie_name;
            if (catName != null && catName !== "") {
                itemPayload.category_name = String(catName).trim();
            }
            const promoDiscAmount = cartItem.promo_disc_amount !== undefined && cartItem.promo_disc_amount !== null
                ? parseFloat(cartItem.promo_disc_amount)
                : null;
            if (promoDiscAmount !== null && Number.isFinite(promoDiscAmount)) {
                itemPayload.promo_disc_amount = promoDiscAmount;
            }
            if (cartItem.promotion_applied === true) {
                itemPayload.promotion_applied = true;
            }
            const promotionId = cartItem.promotion_id !== undefined && cartItem.promotion_id !== null
                ? Number(cartItem.promotion_id)
                : null;
            if (promotionId !== null && Number.isFinite(promotionId) && promotionId > 0) {
                itemPayload.promotion_id = promotionId;
            }
            const availBoxRaw = cartItem.availableBoxQty ?? cartItem.available_box_qty;
            const availPieceRaw = cartItem.availablePieceQty ?? cartItem.available_piece_qty;
            if (availBoxRaw !== undefined && availBoxRaw !== null && availBoxRaw !== "") {
                const nb = parseFloat(availBoxRaw);
                if (Number.isFinite(nb)) itemPayload.availableBoxQty = nb;
            }
            if (availPieceRaw !== undefined && availPieceRaw !== null && availPieceRaw !== "") {
                const np = parseFloat(availPieceRaw);
                if (Number.isFinite(np)) itemPayload.availablePieceQty = np;
            }
            const lineComment = String(
                cartItem.comment ||
                cartItem.discount_comment ||
                cartItem.manualOverrides?.discount_comment ||
                ""
            ).trim();
            const isOfferPayloadLine =
                isGeneratedLine ||
                promoType === "GET" ||
                promoType === "FREE" ||
                isCartLineOfferDisplayRole(cartItem);
            if (lineComment && !isOfferPayloadLine) {
                itemPayload.comment = lineComment;
            }
            itemsMap.set(key, itemPayload);
        }
    });

    const items = Array.from(itemsMap.values());

    const payload = {
        customer_id: customerId,
        channel: channel,
        order_date: orderDate,
        ...(orderId != null && { order_id: orderId }),
        items
    };
    if (orderTime) {
        payload.order_time = orderTime;
    }
    return payload;
};

const normalizeMergePieceType = (val) => {
    const raw = String(val || "Box").trim();
    return raw.charAt(0).toUpperCase() + raw.slice(1).toLowerCase();
};

const normalizePromoTypeToken = (val) => {
    const token = String(val ?? "").trim().toUpperCase();
    if (token === "BUY" || token === "GET" || token === "FREE") {
        return token;
    }
    return "";
};

const isTruthyGenerated = (entity) =>
    entity?.generated === true || entity?.generated === "1" || entity?.generated === 1;

const isFreeProductFlag = (entity) =>
    String(entity?.free_product ?? "").trim().toUpperCase() === "Y";

const cartLinePromoRole = (entity) => {
    if (!entity) return "standard";
    if (isCartLineOfferDisplayRole(entity)) return "get_or_free";
    const promoType = normalizePromoTypeToken(
        entity.promo_type ?? entity.promoType ?? entity.promosType ?? entity.buyOrGet
    );
    if (promoType === "BUY") return "buy_promo";
    return "standard";
};

const promoResponseRole = (promoItem) => {
    if (!promoItem) return "standard";
    if (promoItem.generated) return "get_or_free";
    const promoType = normalizePromoTypeToken(promoItem.promo_type);
    if (promoType === "GET" || promoType === "FREE") return "get_or_free";
    if (promoType === "BUY") return "buy_promo";
    return "standard";
};

const linePromoId = (entity) => {
    const raw = entity?.promotion_id ?? entity?.promoId ?? entity?.promosId ?? entity?.promo_id ?? entity?.promos_id ?? 0;
    const parsed = parseInt(String(raw), 10);
    return Number.isFinite(parsed) ? parsed : 0;
};

const rolesCompatibleForPromoMerge = (responseRole, candidateRole) => {
    if (responseRole === candidateRole) return true;
    if (responseRole === "get_or_free" || candidateRole === "get_or_free") {
        return responseRole === candidateRole;
    }
    if (
        (responseRole === "buy_promo" || responseRole === "standard") &&
        (candidateRole === "buy_promo" || candidateRole === "standard")
    ) {
        return true;
    }
    return false;
};

const scorePromoLineMatch = (promoItem, candidate) => {
    const candidatePid = String(candidate?.product_id ?? candidate?.productId ?? "");
    const promoPid = String(promoItem?.product_id ?? promoItem?.productId ?? "");
    if (!candidatePid || candidatePid !== promoPid) {
        return -1;
    }
    if (
        normalizeMergePieceType(candidate?.order_type || candidate?.type || candidate?.piece_type) !==
        normalizeMergePieceType(promoItem?.piece_type)
    ) {
        return -1;
    }

    const responseRole = promoResponseRole(promoItem);
    const candidateRole = cartLinePromoRole(candidate);
    if (!rolesCompatibleForPromoMerge(responseRole, candidateRole)) {
        return -1;
    }

    let score = 10;
    score += 50;

    const responsePromoId = linePromoId(promoItem);
    const candidatePromoId = linePromoId(candidate);
    if (responsePromoId > 0 && candidatePromoId > 0 && responsePromoId === candidatePromoId) {
        score += 30;
    } else if (responsePromoId === 0 && candidatePromoId === 0) {
        score += 5;
    }

    const responsePromoType = normalizePromoTypeToken(promoItem?.promo_type);
    const candidatePromoType = normalizePromoTypeToken(
        candidate?.promo_type ?? candidate?.promoType ?? candidate?.promosType ?? candidate?.buyOrGet
    );
    if (responsePromoType && candidatePromoType && responsePromoType === candidatePromoType) {
        score += 20;
    }

    if (isTruthyGenerated(promoItem) === isTruthyGenerated(candidate)) {
        score += 15;
    }
    if (isFreeProductFlag(promoItem) === isFreeProductFlag(candidate)) {
        score += 10;
    }

    return score;
};

const findBestLineMatch = (promoItem, candidates, consumedKeys, keyOf) => {
    let bestIndex = -1;
    let bestScore = -1;
    for (let i = 0; i < candidates.length; i += 1) {
        const key = keyOf(candidates[i], i);
        if (consumedKeys.has(key)) continue;
        const score = scorePromoLineMatch(promoItem, candidates[i]);
        if (score > bestScore) {
            bestScore = score;
            bestIndex = i;
        }
    }
    if (bestIndex >= 0 && bestScore >= 10) {
        return { item: candidates[bestIndex], index: bestIndex, key: keyOf(candidates[bestIndex], bestIndex) };
    }
    return { item: null, index: -1, key: null };
};

const effectiveMergedLineQty = (item) => {
    const raw = item?.allot_qty ?? item?.qty ?? item?.quantity ?? item?.accept_qty;
    const n = parseFloat(raw);
    return Number.isFinite(n) ? n : 0;
};

export const enrichCartItemsWithProductCodes = (items, productSources = []) => {
    if (!Array.isArray(items) || items.length === 0) {
        return items;
    }
    const codeByProductId = new Map();
    const register = (entry) => {
        if (!entry || typeof entry !== "object") {
            return;
        }
        const productId = String(entry.product_id ?? entry.id ?? "").trim();
        const code = String(entry.code ?? entry.product_code ?? entry.productCode ?? "").trim();
        if (productId && code && !codeByProductId.has(productId)) {
            codeByProductId.set(productId, code);
        }
    };
    (Array.isArray(productSources) ? productSources : []).forEach(register);
    if (codeByProductId.size === 0) {
        return items;
    }
    return items.map((item) => {
        const existingCode = String(item?.code ?? item?.product_code ?? "").trim();
        if (existingCode) {
            return item;
        }
        const productId = String(item?.product_id ?? item?.productId ?? "").trim();
        const resolvedCode = productId ? codeByProductId.get(productId) : "";
        if (!resolvedCode) {
            return item;
        }
        return {
            ...item,
            code: resolvedCode,
            product_code: resolvedCode,
        };
    });
};

export const mergePromotionResponseWithCart = (cartListData, promotionResponse, existingOrderProducts = [], currentCartItems = [], keepZeroQtyLines = false) => {
    if (!Array.isArray(promotionResponse?.data?.items)) {
        return cartListData?.data || [];
    }

    const normalizeMergePieceTypeLocal = (val) => normalizeMergePieceType(val);

    const mergeLineKey = (productId, pieceTypeRaw) =>
        `${String(productId ?? "")}_${normalizeMergePieceTypeLocal(pieceTypeRaw)}`;

    const firstFiniteQty = (...candidates) => {
        for (let i = 0; i < candidates.length; i += 1) {
            const c = candidates[i];
            if (c === undefined || c === null || c === "") continue;
            const n = parseFloat(c);
            if (Number.isFinite(n)) return n;
        }
        return 0;
    };

    const promotionItems = promotionResponse.data.items;
    const cartData = cartListData?.data || [];
    const promotionGetQuantity = promotionResponse.data?.get_quantity !== undefined && promotionResponse.data?.get_quantity !== null && promotionResponse.data?.get_quantity !== ""
        ? parseInt(promotionResponse.data.get_quantity)
        : null;

    const allotQtyMap = new Map();
    currentCartItems.forEach((item) => {
        const key = mergeLineKey(item.product_id, item.order_type || item.type);
        if (item.allot_qty !== undefined && item.allot_qty !== null) {
            allotQtyMap.set(key, String(item.allot_qty));
        }
    });

    const orderQtyMap = new Map();
    cartData.forEach((item) => {
        const key = mergeLineKey(item.product_id, item.order_type || item.type);
        if (item.order_qty !== undefined && item.order_qty !== null && item.order_qty !== "") {
            orderQtyMap.set(key, String(item.order_qty));
        }
    });

    const promoTypeMap = new Map();
    cartData.forEach((item) => {
        const baseKey = mergeLineKey(item.product_id, item.order_type || item.type);
        const role = cartLinePromoRole(item);
        const roleKey = `${baseKey}::${role}`;
        let promoType = null;
        if (item.promo_type !== undefined && item.promo_type !== null && item.promo_type !== "") {
            promoType = item.promo_type;
        } else if (item.buyOrGet !== undefined && item.buyOrGet !== null && item.buyOrGet !== "") {
            const buyOrGetUpper = String(item.buyOrGet).toUpperCase();
            promoType = buyOrGetUpper === "BUY" ? "BUY" : buyOrGetUpper === "GET" ? "GET" : buyOrGetUpper === "FREE" ? "FREE" : null;
        }
        if (promoType !== null) {
            promoTypeMap.set(roleKey, promoType);
        }
    });

    const productCodeById = new Map();
    const registerProductCode = (item) => {
        if (!item || typeof item !== "object") {
            return;
        }
        const productId = String(item.product_id ?? item.id ?? item.buy_product_id ?? item.get_product_id ?? "").trim();
        const code = String(
            item.code ??
            item.product_code ??
            item.productCode ??
            item.buy_product_code ??
            item.get_product_code ??
            ""
        ).trim();
        if (productId && code && !productCodeById.has(productId)) {
            productCodeById.set(productId, code);
        }
    };
    cartData.forEach(registerProductCode);
    currentCartItems.forEach(registerProductCode);
    (existingOrderProducts || []).forEach(registerProductCode);
    promotionItems.forEach(registerProductCode);
    const promotionSummary = promotionResponse?.data?.promotion_summary;
    if (promotionSummary && typeof promotionSummary === "object") {
        const promoLists = [
            ...(promotionSummary.applied_promotions || []),
            ...(promotionSummary.available_promotions || []),
            ...(promotionSummary.applicable_promotions || []),
        ];
        promoLists.forEach((promo) => {
            [
                ...(promo.buy_products || []),
                ...(promo.get_products || []),
                ...(promo.offer_products || []),
            ].forEach(registerProductCode);
        });
    }

    const orderedFromApi = [];
    const consumedCartKeys = new Set();
    const consumedOriginalKeys = new Set();

    promotionItems.forEach((promoItem, idx) => {
        const cartMatch = findBestLineMatch(
            promoItem,
            cartData,
            consumedCartKeys,
            (item, index) => `cart:${index}:${item?.id ?? ""}`
        );
        const cartItem = cartMatch.item;
        if (cartMatch.key) {
            consumedCartKeys.add(cartMatch.key);
        }

        const existingMatch = findBestLineMatch(
            promoItem,
            existingOrderProducts,
            consumedOriginalKeys,
            (item, index) => `orig:${item?.id ?? index}`
        );
        const existingProduct = existingMatch.item;
        if (existingMatch.key) {
            consumedOriginalKeys.add(existingMatch.key);
        }

        const cartKey = mergeLineKey(promoItem.product_id, promoItem.piece_type);

        const code =
            promoItem.product_code ||
            promoItem.code ||
            productCodeById.get(String(promoItem.product_id ?? "")) ||
            cartItem?.code ||
            cartItem?.product_code ||
            existingProduct?.code ||
            existingProduct?.product_code ||
            "";
        const name =
            promoItem.product_name ||
            cartItem?.name ||
            cartItem?.product_name ||
            cartItem?.desc ||
            existingProduct?.product_name ||
            existingProduct?.name ||
            "";
        const orderType = promoItem.piece_type || "Box";
        const unitOrderType = String(promoItem.unit_order_type ?? "").trim();

        const originalUnitPrice = parseFloat(promoItem.unit_price || 0);
        const promoUnitPrice = parseFloat(promoItem.promo_unit_price || 0);
        const baseUnitPrice = parseFloat(promoItem.base_unit_price || promoItem.unit_price || 0);
        const standardPrice = parseFloat(promoItem.standard_price || cartItem?.standard_price || existingProduct?.standard_price || 0);
        const hasExplicitResponseQty =
            promoItem.quantity !== undefined &&
            promoItem.quantity !== null &&
            String(promoItem.quantity).trim() !== "";
        let qty = parseFloat(promoItem.quantity);
        if (!Number.isFinite(qty)) qty = 0;
        const preservedOrderQty = orderQtyMap.get(cartKey);
        const itemGetQuantity = promoItem.get_quantity !== undefined && promoItem.get_quantity !== null && promoItem.get_quantity !== ""
            ? parseInt(promoItem.get_quantity)
            : null;
        let orderQty = null;

        if (promoItem.order_qty !== undefined && promoItem.order_qty !== null) {
            if (typeof promoItem.order_qty === 'number' && !isNaN(promoItem.order_qty)) {
                orderQty = promoItem.order_qty;
            } else if (typeof promoItem.order_qty === 'string' && promoItem.order_qty !== "") {
                const parsed = parseFloat(promoItem.order_qty);
                if (!isNaN(parsed)) {
                    orderQty = parsed;
                }
            }
        }

        if (orderQty === null && preservedOrderQty !== undefined) {
            const parsed = parseFloat(preservedOrderQty);
            if (!isNaN(parsed)) {
                orderQty = parsed;
            }
        }

        if (orderQty === null && cartItem?.order_qty !== undefined && cartItem.order_qty !== null && cartItem.order_qty !== "") {
            const parsed = parseFloat(cartItem.order_qty);
            if (!isNaN(parsed)) {
                orderQty = parsed;
            }
        }

        if (orderQty === null && existingProduct?.order_qty !== undefined && existingProduct.order_qty !== null && existingProduct.order_qty !== "") {
            const parsed = parseFloat(existingProduct.order_qty);
            if (!isNaN(parsed)) {
                orderQty = parsed;
            }
        }
        const vatRate = promoItem.tax_rate || 0;
        const lineDiscount = promoItem.line_discount || 0;
        const itemDiscount = parseFloat(promoItem.item_discount || 0) || 0;
        const itemDiscountType = promoItem.item_discount_type || "amount";
        const discountPercentage = parseFloat(promoItem.discount_percentage || 0) || 0;
        const discountAmount = parseFloat(promoItem.discount_amount || 0) || 0;
        const manualDiscountRaw = cartItem?.manualOverrides?.discount;
        const hasManualDiscountOverride = manualDiscountRaw !== undefined && manualDiscountRaw !== null && String(manualDiscountRaw).trim() !== "";
        const hasExplicitCartDiscount = cartItem?.discount !== undefined && cartItem?.discount !== null && cartItem?.discount !== "";
        const cartDiscountValue = hasExplicitCartDiscount ? parseFloat(cartItem.discount) : null;
        let mergedDiscountType = itemDiscountType;
        let mergedDiscount = itemDiscount;
        if (hasManualDiscountOverride) {
            mergedDiscount = parseFloat(manualDiscountRaw) || 0;
            mergedDiscountType = cartItem.manualOverrides?.discount_type || cartItem.discount_type || cartItem.productWiseDiscountType || itemDiscountType;
        } else if (hasExplicitCartDiscount && Number.isFinite(cartDiscountValue)) {
            mergedDiscount = cartDiscountValue;
            mergedDiscountType = cartItem.discount_type || cartItem.productWiseDiscountType || itemDiscountType;
        }
        const mergedDiscountTypeNorm = String(mergedDiscountType).trim().toLowerCase();
        const resolvedItemDiscountType = (mergedDiscountTypeNorm === "fixed" || mergedDiscountTypeNorm === "£" || mergedDiscountTypeNorm === "amount") ? "amount" : "percentage";
        const promoDiscPercentage = promoItem.promo_disc_percentage !== undefined && promoItem.promo_disc_percentage !== null ? parseFloat(promoItem.promo_disc_percentage) : null;
        const promoDiscAmount = promoItem.promo_disc_amount !== undefined && promoItem.promo_disc_amount !== null ? parseFloat(promoItem.promo_disc_amount) : null;
        const generated = promoItem.generated || false;
        const promotionApplied = promoItem.promotion_applied || false;
        const promoIdForDiscount = parseInt(promoItem.promotion_id || 0, 10) || 0;
        // check-promotion names the line promo discount promo_disc_amount /
        // line_discount / discount_amount depending on the promotion type.
        const promotionDiscount = promotionApplied || promoIdForDiscount > 0
            ? [
                promoItem.promotion_discount,
                promoItem.promo_disc_amount,
                promoItem.promo_discount_amount,
                promoItem.line_discount,
                promoItem.discount_amount,
            ].reduce((resolved, candidate) => {
                if (resolved > 0.0001) return resolved;
                const parsed = parseFloat(candidate);
                return Number.isFinite(parsed) && parsed > 0.0001 ? parsed : resolved;
            }, 0)
            : 0;
        const responseIsPaidBuy =
            !generated &&
            !promotionApplied &&
            Math.max(promoUnitPrice, originalUnitPrice) > 0.0001;
        let lineSubtotal = parseFloat(promoItem.line_subtotal || promoItem.sub_total || 0);
        let lineVat = parseFloat(promoItem.line_vat || promoItem.line_tax || 0);

        const cartSellingPrice = cartItem
            ? Math.max(
                parseFloat(cartItem.unit_price ?? cartItem.price ?? 0) || 0,
                parseFloat(cartItem.price ?? 0) || 0
            )
            : 0;
        const paidSellingPrice = Math.max(
            promoUnitPrice > 0.0001 ? promoUnitPrice : 0,
            originalUnitPrice > 0.0001 ? originalUnitPrice : 0,
            !isCartLineOfferDisplayRole(cartItem) && cartSellingPrice > 0.0001 ? cartSellingPrice : 0
        );
        const treatAsPaidBuyLine =
            responseIsPaidBuy ||
            (paidSellingPrice > 0.0001 &&
            qty > 0 &&
            !generated &&
            (!cartItem || !isCartLineOfferDisplayRole(cartItem)));
        const qtyForLineBase = Number.isFinite(qty) ? qty : 0;
        const lineBaseBeforeDiscount = paidSellingPrice * qtyForLineBase;
        const isFullDiscountLine = isFullLineItemDiscount(
            mergedDiscount,
            resolvedItemDiscountType,
            lineBaseBeforeDiscount,
            qtyForLineBase
        );
        if (treatAsPaidBuyLine && lineSubtotal <= 0.0001 && !isFullDiscountLine) {
            lineSubtotal = parseFloat((paidSellingPrice * qty).toFixed(2));
        }

        const isDiscounted = promotionApplied && originalUnitPrice > promoUnitPrice && promoUnitPrice > 0;
        const isFree = promotionApplied && promoUnitPrice === 0;

        const responseOriginalPrice = promoItem.original_price !== undefined && promoItem.original_price !== null && promoItem.original_price !== ""
            ? parseFloat(promoItem.original_price)
            : null;

        const hasManualUnitPriceOverride =
            cartItem?.manualOverrides?.unit_price !== undefined &&
            cartItem?.manualOverrides?.unit_price !== null &&
            String(cartItem.manualOverrides.unit_price).trim() !== "";
        const cartEditedUnitPrice = hasManualUnitPriceOverride
            ? parseFloat(cartItem.manualOverrides.unit_price)
            : parseFloat(cartItem?.unit_price ?? cartItem?.price ?? NaN);
        const matchesResponseOriginal =
            Number.isFinite(responseOriginalPrice) &&
            Number.isFinite(cartEditedUnitPrice) &&
            Math.abs(cartEditedUnitPrice - responseOriginalPrice) < 0.0001;
        const hasCartEditedUnitPrice =
            !promotionApplied &&
            !hasManualUnitPriceOverride &&
            Number.isFinite(cartEditedUnitPrice) &&
            cartEditedUnitPrice > 0 &&
            !generated &&
            (!cartItem || !isCartLineOfferDisplayRole(cartItem)) &&
            Math.abs(cartEditedUnitPrice - originalUnitPrice) > 0.0001 &&
            !matchesResponseOriginal;
        const unitPrice = (() => {
            if (generated) {
                return Number.isFinite(promoUnitPrice) && promoUnitPrice >= 0 ? promoUnitPrice : 0;
            }
            if (hasManualUnitPriceOverride && Number.isFinite(cartEditedUnitPrice) && cartEditedUnitPrice > 0) {
                return cartEditedUnitPrice;
            }
            if (promotionApplied) {
                if (Number.isFinite(promoUnitPrice) && promoUnitPrice > 0.0001) {
                    return promoUnitPrice;
                }
                if (Number.isFinite(originalUnitPrice) && originalUnitPrice > 0.0001) {
                    return originalUnitPrice;
                }
                if (Number.isFinite(promoUnitPrice) && promoUnitPrice >= 0) {
                    return promoUnitPrice;
                }
                return 0;
            }
            if (hasCartEditedUnitPrice) {
                return cartEditedUnitPrice;
            }
            return originalUnitPrice;
        })();
        let discountPercent = 0;
        let originalPrice = responseOriginalPrice !== null && Number.isFinite(responseOriginalPrice)
            ? responseOriginalPrice
            : (promotionApplied && originalUnitPrice > 0 ? originalUnitPrice : (baseUnitPrice > 0 ? baseUnitPrice : originalUnitPrice));
        let discountPrice = promoUnitPrice;

        const fallbackOriginalPrice = promotionApplied && originalUnitPrice > 0 ? originalUnitPrice : (baseUnitPrice > 0 ? baseUnitPrice : originalUnitPrice);
        if (isDiscounted) {
            originalPrice = (responseOriginalPrice !== null && Number.isFinite(responseOriginalPrice) && responseOriginalPrice > 0)
                ? responseOriginalPrice
                : fallbackOriginalPrice;
            discountPrice = promoUnitPrice;
        } else if (isFree) {
            originalPrice = (responseOriginalPrice !== null && Number.isFinite(responseOriginalPrice) && responseOriginalPrice > 0)
                ? responseOriginalPrice
                : fallbackOriginalPrice;
            discountPrice = 0;
        }

        const isPromoted = promotionApplied ? "1" : "0";
        const promoId = promoItem.promotion_id || 0;
        const responsePromoType = promoItem.promo_type || null;
        const isResponsePromoTypeValid = responsePromoType === "BUY" || responsePromoType === "GET" || responsePromoType === "FREE";

        let promoType = null;

        if (isResponsePromoTypeValid) {
            promoType = responsePromoType;
        } else if (!responseIsPaidBuy) {
            const responseRole = promoResponseRole(promoItem);
            const roleKey = `${cartKey}::${responseRole}`;
            let preservedPromoType = promoTypeMap.get(roleKey);
            if (preservedPromoType === undefined && cartItem) {
                preservedPromoType = promoTypeMap.get(`${cartKey}::${cartLinePromoRole(cartItem)}`);
            }
            if (
                preservedPromoType !== undefined &&
                (preservedPromoType === "BUY" || preservedPromoType === "GET" || preservedPromoType === "FREE") &&
                rolesCompatibleForPromoMerge(
                    promoResponseRole({ promo_type: preservedPromoType }),
                    responseRole
                )
            ) {
                promoType = preservedPromoType;
            } else if (cartItem?.promo_type !== undefined && cartItem.promo_type !== null && cartItem.promo_type !== "" && (cartItem.promo_type === "BUY" || cartItem.promo_type === "GET" || cartItem.promo_type === "FREE")) {
                promoType = cartItem.promo_type;
            } else if (cartItem?.buyOrGet !== undefined && cartItem.buyOrGet !== null && cartItem.buyOrGet !== "") {
                const buyOrGetUpper = String(cartItem.buyOrGet).toUpperCase();
                promoType = buyOrGetUpper === "BUY" ? "BUY" : buyOrGetUpper === "GET" ? "GET" : buyOrGetUpper === "FREE" ? "FREE" : null;
            } else if (existingProduct?.buyOrGet !== undefined && existingProduct.buyOrGet !== null && existingProduct.buyOrGet !== "") {
                const buyOrGetUpper = String(existingProduct.buyOrGet).toUpperCase();
                promoType = buyOrGetUpper === "BUY" ? "BUY" : buyOrGetUpper === "GET" ? "GET" : buyOrGetUpper === "FREE" ? "FREE" : null;
            }
        }

        if (cartItem && (promoType === "GET" || promoType === "FREE") && !hasExplicitResponseQty && !promotionApplied) {
            const rawCartQty =
                cartItem.allot_qty !== undefined && cartItem.allot_qty !== null && cartItem.allot_qty !== ""
                    ? cartItem.allot_qty
                    : cartItem.qty !== undefined && cartItem.qty !== null && cartItem.qty !== ""
                        ? cartItem.qty
                        : cartItem.quantity;
            const cq = parseFloat(rawCartQty);
            if (Number.isFinite(cq) && cq >= 0) {
                qty = cq;
            }
        }

        if (promotionApplied && lineDiscount > 0 && qty > 0 && originalUnitPrice > 0) {
            const totalBeforeDiscount = originalUnitPrice * qty;
            discountPercent = (lineDiscount / totalBeforeDiscount) * 100;
            discountPercent = Math.min(Math.max(discountPercent, 0), 100);
        }

        let boxPrice = parseFloat(cartItem?.sales_box_price || existingProduct?.price || 0) || 0;
        let piecePrice = parseFloat(cartItem?.sales_piece_price || existingProduct?.split_price || 0) || 0;
        const split = parseInt(cartItem?.split || existingProduct?.split || 1);

        if (isDiscounted || isFree) {
            if (orderType === "Box") {
                boxPrice = originalPrice;
            } else {
                piecePrice = originalPrice;
            }
        }

        const size = cartItem?.size || existingProduct?.size || "";
        const innerEanNumber = existingProduct?.inner_ean_number || "";
        const outerEanNumber = existingProduct?.outer_ean_number || "";
        const categoryName = (promoItem.category_name != null && promoItem.category_name !== "")
            ? String(promoItem.category_name).trim()
            : (cartItem?.category_name ?? cartItem?.categorie_name ?? existingProduct?.category_name ?? existingProduct?.categorie_name ?? "");
        const price1 = parseFloat(existingProduct?.price_1 || 0);
        const price2 = parseFloat(existingProduct?.price_2 || 0);
        const price3 = parseFloat(existingProduct?.price_3 || 0);
        const price4 = parseFloat(existingProduct?.price_4 || 0);
        const price5 = parseFloat(existingProduct?.price_5 || 0);
        const piecePrice1 = parseFloat(existingProduct?.piece_price1 || 0);
        const piecePrice2 = parseFloat(existingProduct?.piece_price2 || 0);
        const piecePrice3 = parseFloat(existingProduct?.piece_price3 || 0);
        const piecePrice4 = parseFloat(existingProduct?.piece_price4 || 0);
        const piecePrice5 = parseFloat(existingProduct?.piece_price5 || 0);
        const cost = parseFloat(existingProduct?.cost || 0);
        const pieceCost = parseFloat(existingProduct?.piece_cost || 0);

        const availableBoxQty = firstFiniteQty(
            promoItem.availableBoxQty,
            promoItem.available_box_qty,
            cartItem?.availableBoxQty,
            cartItem?.available_box_qty,
            existingProduct?.availableBoxQty,
            existingProduct?.available_box_qty
        );
        const availablePieceQty = firstFiniteQty(
            promoItem.availablePieceQty,
            promoItem.available_piece_qty,
            cartItem?.availablePieceQty,
            cartItem?.available_piece_qty,
            existingProduct?.availablePieceQty,
            existingProduct?.available_piece_qty
        );

        const resolveMaximumOrderQuantity = () => {
            const candidates = [
                promoItem.maximum_order_quantity,
                promoItem.maximumOrderQuantity,
                cartItem?.maximum_order_quantity,
                cartItem?.maximumOrderQuantity,
                existingProduct?.maximum_order_quantity,
                existingProduct?.maximumOrderQuantity,
            ];
            for (let i = 0; i < candidates.length; i += 1) {
                const c = candidates[i];
                if (c === undefined || c === null || c === "") continue;
                const n = typeof c === "number" ? c : parseFloat(c);
                if (Number.isFinite(n) && n > 0) return n;
            }
            return null;
        };
        const maximumOrderQuantityResolved = resolveMaximumOrderQuantity();

        const productDiscountApplicable = parseInt(existingProduct?.productDiscountApplicable || 0);
        const discountedBoxPrice = parseFloat(existingProduct?.discountedBoxPrice || 0);
        const discountedSplitPrice = parseFloat(existingProduct?.discountedSplitPrice || 0);
        const isContractPriceApplied = cartItem?.isContractPriceApplied || cartItem?.is_contract_price_applied || existingProduct?.isContractPriceApplied || existingProduct?.is_contract_price_applied || "N";

        const preservedAllotQty = allotQtyMap.get(cartKey);
        const promoQtyAuthoritative =
            hasExplicitResponseQty ||
            generated ||
            promotionApplied ||
            promoType === "GET" ||
            promoType === "FREE";
        const allotQty = promoQtyAuthoritative
            ? String(qty)
            : preservedAllotQty !== undefined
                ? preservedAllotQty
                : String(qty);
        const promotionDisplayName =
            promoItem.promotion_name ||
            promoItem.promotionName ||
            promoItem.promo_name ||
            promoItem.promos_name ||
            promoItem.promoName ||
            cartItem?.promotion_name ||
            cartItem?.promotionName ||
            cartItem?.promo_name ||
            existingProduct?.promotion_name ||
            existingProduct?.promotionName ||
            existingProduct?.promo_name ||
            existingProduct?.promos_name ||
            existingProduct?.promoName ||
            "";

        const resolvedSlabId =
            resolveSlabIdFromEntity(promoItem) ||
            resolveSlabIdFromEntity(cartItem) ||
            resolveSlabIdFromEntity(existingProduct);

        let resolvedOrderQty = orderQty;
        if (resolvedOrderQty === null || !Number.isFinite(resolvedOrderQty) || resolvedOrderQty <= 0) {
            const q = Number.isFinite(qty) ? qty : 0;
            if (q > 0) resolvedOrderQty = q;
        }

        const promoTypeForId = promoType || "NONE";
        const isOfferMerged =
            generated ||
            promoType === "GET" ||
            promoType === "FREE" ||
            isCartLineOfferDisplayRole(promoItem);
        const keepLineComment =
            !isOfferMerged && Number.isFinite(parseFloat(mergedDiscount)) && parseFloat(mergedDiscount) > 0.0001;
        const resolvedLineComment = keepLineComment
            ? String(
                promoItem.comment ||
                promoItem.discount_comment ||
                cartItem?.comment ||
                cartItem?.discount_comment ||
                cartItem?.manualOverrides?.discount_comment ||
                ""
            ).trim()
            : "";
        let mergedManualOverrides = cartItem?.manualOverrides;
        if (mergedManualOverrides && !keepLineComment) {
            const { discount_comment: _omitComment, ...restOverrides } = mergedManualOverrides;
            mergedManualOverrides = restOverrides;
            if (Object.keys(mergedManualOverrides).length === 0) {
                mergedManualOverrides = undefined;
            }
        }
        const fallbackItemId = generated
            ? `promo_gen_${promoItem.product_id}_${promoTypeForId}_${idx}_${Date.now()}`
            : `ed_promo_${promoItem.product_id}_${orderType}_${promoTypeForId}_${Date.now()}`;
        const resolvedItemId =
            cartItem?.id !== undefined && cartItem?.id !== null && String(cartItem.id).trim() !== ""
                ? String(cartItem.id)
                : fallbackItemId;

        const mergedItem = {
            id: resolvedItemId,
            product_id: String(promoItem.product_id),
            productId: String(promoItem.product_id),
            code,
            product_code: code,
            name,
            product_name: name,
            desc: name,
            type: orderType,
            order_type: orderType,
            ...(PREFIX === "atr" && unitOrderType ? { unit_order_type: unitOrderType } : {}),
            split,
            noOfPieceInBox: split,
            sales_box_price: boxPrice,
            sales_piece_price: piecePrice,
            boxPrice,
            piecePrice,
            category_name: categoryName,
            qty: String(qty),
            price: unitPrice,
            unit_price: unitPrice,
            original_price: originalPrice,
            discount_price: discountPrice,
            discount: String(mergedDiscount),
            item_discount: String(mergedDiscount),
            discount_type: resolvedItemDiscountType,
            productWiseDiscountType: resolvedItemDiscountType,
            rate: String(vatRate),
            tax_rate: String(vatRate),
            tax_code: resolveProductTaxCode(cartItem) || resolveProductTaxCode(promoItem) || resolveProductTaxCode(existingProduct),
            is_promoted: isPromoted,
            promoId: promoId,
            promosId: promoId,
            promoType: promoType,
            promosType: promoType,
            generated: generated,
            free_product: generated ? "Y" : "N",
            line_discount: lineDiscount,
            promo_unit_price: promoUnitPrice,
            base_unit_price: baseUnitPrice,
            standard_price: Number.isFinite(standardPrice) && standardPrice >= 0 ? standardPrice : 0,
            price_1: price1,
            price_2: price2,
            price_3: price3,
            price_4: price4,
            price_5: price5,
            piece_price1: piecePrice1,
            piece_price2: piecePrice2,
            piece_price3: piecePrice3,
            piece_price4: piecePrice4,
            piece_price5: piecePrice5,
            cost,
            piece_cost: pieceCost,
            pieceCost,
            itemTax: lineVat,
            vat: lineVat,
            tax: lineVat,
            subtotal: lineSubtotal,
            sub_total: lineSubtotal,
            line_subtotal: lineSubtotal,
            useBackendCalculations: true,
            availableBoxQty,
            availablePieceQty,
            ...(maximumOrderQuantityResolved !== null
                ? {
                    maximum_order_quantity: maximumOrderQuantityResolved,
                    maximumOrderQuantity: maximumOrderQuantityResolved,
                }
                : {}),
            size,
            inner_ean_number: innerEanNumber,
            outer_ean_number: outerEanNumber,
            innerEanNumber,
            outerEanNumber,
            discountedBoxPrice,
            discountedSplitPrice,
            productDiscountApplicable,
            accept_qty: allotQty,
            allot_qty: allotQty,
            order_qty: String(Number.isFinite(qty) ? qty : 0),
            original_order_qty: (() => {
                const preserved = [
                    cartItem?.original_order_qty,
                    existingProduct?.original_order_qty,
                    existingProduct?.order_qty,
                ].reduce((found, candidate) => {
                    if (found != null) return found;
                    const n = parseFloat(candidate);
                    return Number.isFinite(n) && n > 0 ? n : found;
                }, null);
                if (preserved != null) return String(preserved);
                if (Number.isFinite(resolvedOrderQty) && resolvedOrderQty > 0) return String(resolvedOrderQty);
                return String(Number.isFinite(qty) ? qty : 0);
            })(),
            ...(promoType !== null ? { promo_type: promoType, buyOrGet: promoType } : {}),
            ...((itemGetQuantity !== null || (promotionGetQuantity !== null && generated)) ? { get_quantity: String(itemGetQuantity !== null ? itemGetQuantity : promotionGetQuantity) } : {}),
            ...(mergedManualOverrides ? { manualOverrides: mergedManualOverrides } : {}),
            discount_comment: resolvedLineComment,
            comment: resolvedLineComment,
            isContractPriceApplied: isContractPriceApplied,
            is_contract_price_applied: isContractPriceApplied,
            discount_percentage: discountPercentage,
            discount_amount: discountAmount,
            ...(promoDiscPercentage !== null ? { promo_disc_percentage: promoDiscPercentage } : {}),
            ...(promoDiscAmount !== null ? { promo_disc_amount: promoDiscAmount } : {}),
            promotion_discount: promotionDiscount,
            ...(promoItem.productWiseDiscountType !== undefined && promoItem.productWiseDiscountType !== null
                ? { api_productWiseDiscountType: promoItem.productWiseDiscountType }
                : {}),
            ...(promoItem.productWiseDiscountValue !== undefined && promoItem.productWiseDiscountValue !== null
                ? { api_productWiseDiscountValue: promoItem.productWiseDiscountValue }
                : {}),
            ...(promoItem.productWiseDiscountPercentage !== undefined && promoItem.productWiseDiscountPercentage !== null
                ? { api_productWiseDiscountPercentage: parseFloat(promoItem.productWiseDiscountPercentage) }
                : {}),
            ...(promoItem.productWiseDiscountAmount !== undefined && promoItem.productWiseDiscountAmount !== null
                ? { api_productWiseDiscountAmount: parseFloat(promoItem.productWiseDiscountAmount) }
                : {}),
            promotion_applied: promotionApplied,
            promotion_id: promoId > 0 ? promoId : null,
            promotion_name: promotionDisplayName,
            promo_name: promotionDisplayName,
            ...(resolvedSlabId ? { slab_id: resolvedSlabId, slabId: resolvedSlabId } : {}),
            ...(promoItem.bundle_id != null && promoItem.bundle_id !== ""
                ? { bundle_id: promoItem.bundle_id }
                : cartItem?.bundle_id != null && cartItem.bundle_id !== ""
                    ? { bundle_id: cartItem.bundle_id }
                    : {}),
        };

        orderedFromApi.push(mergedItem);
    });

    // After check-promotion the API items list is authoritative. Only keep cart lines
    // that were not matched and are not represented in the promotion response (e.g.
    // stale client-side duplicates of the same buy line).
    const cartOnlySupplement = cartData.filter((cartItem, index) => {
        const cartKey = `cart:${index}:${cartItem?.id ?? ""}`;
        if (consumedCartKeys.has(cartKey)) {
            return false;
        }
        if (cartLinePromoRole(cartItem) === "get_or_free") {
            return false;
        }
        if (!keepZeroQtyLines && effectiveMergedLineQty(cartItem) <= 0) {
            return false;
        }
        return !promotionItems.some((pi) => {
            if (scorePromoLineMatch(pi, cartItem) < 10) return false;
            return rolesCompatibleForPromoMerge(
                promoResponseRole(pi),
                cartLinePromoRole(cartItem)
            );
        });
    });

    const taxByProductId = new Map();
    [...orderedFromApi, ...cartData].forEach((line) => {
        const pid = String(line?.product_id ?? line?.productId ?? "");
        const code = resolveProductTaxCode(line);
        if (!pid || !code || taxByProductId.has(pid)) return;
        taxByProductId.set(pid, {
            tax_code: code,
            rate: line.rate ?? line.tax_rate,
        });
    });
    const cartOnlyWithTax = cartOnlySupplement.map((cartItem) => {
        if (resolveProductTaxCode(cartItem)) return cartItem;
        const tax = taxByProductId.get(String(cartItem?.product_id ?? cartItem?.productId ?? ""));
        if (!tax) return cartItem;
        return {
            ...cartItem,
            tax_code: tax.tax_code,
            rate: cartItem.rate || tax.rate,
            tax_rate: cartItem.tax_rate || tax.rate,
        };
    });

    const ordered = [...orderedFromApi, ...cartOnlyWithTax];
    return keepZeroQtyLines ? ordered : ordered.filter((item) => effectiveMergedLineQty(item) > 0);
};

export const transformProductDetailsToCart = (productDetails) => {
    if (!Array.isArray(productDetails) || productDetails.length === 0) {
        return [];
    }

    return productDetails.map((product) => {
        const normalizePieceType = (val) => {
            const raw = String(val || "Box").trim();
            return raw.charAt(0).toUpperCase() + raw.slice(1).toLowerCase();
        };
        const productId = String(product.product_id || product.id || "");
        const qtyRaw = product.quantity ?? product.qty ?? product.unit_quantity;
        const qty = Number.isFinite(parseFloat(qtyRaw)) ? parseFloat(qtyRaw) : 0;
        let parsedOrderQty = null;
        if (product.order_qty !== undefined && product.order_qty !== null && product.order_qty !== "") {
            const o = parseFloat(product.order_qty);
            if (Number.isFinite(o)) parsedOrderQty = o;
        }
        const orderQty = parsedOrderQty !== null && parsedOrderQty > 0
            ? parsedOrderQty
            : (qty > 0 ? qty : (parsedOrderQty !== null ? parsedOrderQty : 0));
        const orderType = normalizePieceType(product.order_type || product.type || "Box");
        const isGenerated =
            product.generated === true ||
            product.generated === "1" ||
            product.generated === 1;
        const isFreeProduct = String(product.free_product ?? "").trim().toUpperCase() === "Y";
        const promoTypeRaw =
            product.promosType ||
            product.promo_type ||
            product.promoType ||
            product.buyOrGet ||
            null;
        const normalizedPromoType = (() => {
            const token = String(promoTypeRaw ?? "").trim().toUpperCase();
            if (token === "BUY" || token === "GET" || token === "FREE") {
                return token;
            }
            return null;
        })();
        const vatRate = parseFloat(product.rate || product.tax_rate || 0);
        const split = parseInt(product.split || 0);

        const originalPrice = parseFloat(product.original_price || product.price || product.unit_price || 0);
        const discountPrice = parseFloat(product.discount_price || 0);
        const isPromoted = String(product.is_promoted || "0") === "1";
        const categoryName = product.category_name || product.categorie_name || "";
        const boxPrice = parseFloat(product.price || product.price_1 || originalPrice || 0);
        const piecePrice = parseFloat(product.split_price || product.piece_price1 || 0);

        let finalBoxPrice = originalPrice;
        let finalPiecePrice = piecePrice;

        if (isPromoted && orderType === "Box") {
            if (discountPrice === 0 || originalPrice === 0) {
                finalBoxPrice = 0;
            } else if (discountPrice < originalPrice && discountPrice > 0) {
                finalBoxPrice = discountPrice;
            }
        } else if (isPromoted && orderType === "Piece") {
            if (discountPrice === 0 || originalPrice === 0) {
                finalPiecePrice = 0;
            } else if (discountPrice < originalPrice && discountPrice > 0 && originalPrice > 0) {
                const discountRatio = discountPrice / originalPrice;
                finalPiecePrice = piecePrice * discountRatio;
            }
        }

        const salesBoxPrice = originalPrice;
        const salesPiecePrice = piecePrice;
        const currentPrice = orderType === "Box" ? finalBoxPrice : finalPiecePrice;
        const unitPrice = parseFloat(product.unit_price ?? currentPrice ?? 0);

        const itemDiscountValue = product.item_discount !== undefined && product.item_discount !== null && product.item_discount !== ""
            ? parseFloat(product.item_discount)
            : null;
        const discountValue = product.discount !== undefined && product.discount !== null && product.discount !== ""
            ? parseFloat(product.discount)
            : null;

        const discountType = (itemDiscountValue > 0 || (discountValue !== null && discountValue > 0))
            ? (product.productWiseDiscountType || product.discount_type || "amount")
            : "amount";
        const isPct = String(discountType).trim().toLowerCase() === "percentage";
        const discount = (isPct && discountValue !== null && discountValue > 0)
            ? discountValue
            : (itemDiscountValue !== null && itemDiscountValue > 0)
                ? itemDiscountValue
                : (discountValue !== null && discountValue > 0)
                    ? discountValue
                    : 0;

        const promotionDisplayName =
            product.promotion_name ||
            product.promotionName ||
            product.promo_name ||
            product.promos_name ||
            product.promoName ||
            "";
        const resolvedSlabId = resolveSlabIdFromEntity(product);
        const promotionIdRaw =
            product.promotion_id ??
            product.promo_id ??
            product.promosId ??
            product.promoId;
        const promotionIdParsed =
            promotionIdRaw !== undefined && promotionIdRaw !== null && promotionIdRaw !== ""
                ? parseInt(promotionIdRaw, 10)
                : NaN;

        const maxOrderQtyRaw = product.maximum_order_quantity ?? product.maximumOrderQuantity;
        const maxOrderQtyParsed =
            maxOrderQtyRaw !== undefined && maxOrderQtyRaw !== null && maxOrderQtyRaw !== ""
                ? parseFloat(maxOrderQtyRaw)
                : NaN;
        const maxOrderQtyFields =
            Number.isFinite(maxOrderQtyParsed) && maxOrderQtyParsed > 0
                ? { maximum_order_quantity: maxOrderQtyParsed, maximumOrderQuantity: maxOrderQtyParsed }
                : {};

        const hasPaidBuyPrice = Number.isFinite(currentPrice) && currentPrice > 0.0001;
        const isPaidBuyLine =
            hasPaidBuyPrice &&
            normalizedPromoType !== "GET" &&
            normalizedPromoType !== "FREE" &&
            !isGenerated;

        const apiLineSubtotal = parseFloat(
            product.line_subtotal ?? product.sub_total ?? product.subtotal ?? 0
        );
        const apiLineVat = parseFloat(
            product.line_vat ?? product.line_tax ?? product.vat ?? product.tax ?? product.item_tax ?? 0
        );
        const hasApiLineAmounts =
            Number.isFinite(apiLineSubtotal) && apiLineSubtotal > 0.0001;
        const promoUnitPriceRaw = product.promo_unit_price;
        const promoUnitPriceParsed =
            promoUnitPriceRaw !== undefined && promoUnitPriceRaw !== null && promoUnitPriceRaw !== ""
                ? parseFloat(promoUnitPriceRaw)
                : NaN;
        const baseUnitPriceParsed = parseFloat(product.base_unit_price ?? unitPrice ?? 0);
        const promotionAppliedFlag =
            product.promotion_applied === true ||
            product.promotion_applied === "true" ||
            product.promotion_applied === 1 ||
            product.promotion_applied === "1";

        return {
            id: String(product.id || product.product_id || ""),
            product_id: productId,
            productId: productId,
            category_name: categoryName,
            code: product.code || "",
            product_code: product.code || "",
            name: product.product_name || "",
            product_name: product.product_name || "",
            desc: product.product_name || "",
            type: orderType,
            order_type: orderType,
            split: split || 0,
            noOfPieceInBox: split || 0,
            sales_box_price: salesBoxPrice,
            sales_piece_price: salesPiecePrice,
            boxPrice: boxPrice,
            piecePrice: piecePrice,
            qty: String(qty),
            price: currentPrice,
            unit_price: unitPrice,
            original_price: originalPrice,
            discount_price: discountPrice,
            discount: String(discount),
            item_discount: product.item_discount !== undefined && product.item_discount !== null ? String(product.item_discount) : String(discount),
            discount_comment: isPaidBuyLine && discount > 0.0001
                ? String(product.discount_comment || product.comment || "").trim()
                : "",
            comment: isPaidBuyLine && discount > 0.0001
                ? String(product.comment || product.discount_comment || "").trim()
                : "",
            discount_type: discountType,
            productWiseDiscountType: discountType,
            rate: String(vatRate),
            tax_rate: String(vatRate),
            tax_code: resolveProductTaxCode(product),
            is_promoted: String(product.is_promoted || "0"),
            promoId: parseInt(product.promosId || product.promo_id || 0),
            promosId: parseInt(product.promosId || product.promo_id || 0),
            promoType: normalizedPromoType || product.promosType || product.promo_type || null,
            promosType: normalizedPromoType || product.promosType || product.promo_type || null,
            promo_type: normalizedPromoType,
            generated: isGenerated,
            free_product: !isPaidBuyLine && (isFreeProduct || isGenerated) ? "Y" : "N",
            price_1: parseFloat(product.price_1 || 0),
            price_2: parseFloat(product.price_2 || 0),
            price_3: parseFloat(product.price_3 || 0),
            price_4: parseFloat(product.price_4 || 0),
            price_5: parseFloat(product.price_5 || 0),
            piece_price1: parseFloat(product.piece_price1 || 0),
            piece_price2: parseFloat(product.piece_price2 || 0),
            piece_price3: parseFloat(product.piece_price3 || 0),
            piece_price4: parseFloat(product.piece_price4 || 0),
            piece_price5: parseFloat(product.piece_price5 || 0),
            cost: parseFloat(product.cost || 0),
            piece_cost: parseFloat(product.piece_cost || 0),
            pieceCost: parseFloat(product.piece_cost || 0),
            itemTax: parseFloat(product.item_tax || apiLineVat || 0),
            subtotal: hasApiLineAmounts ? apiLineSubtotal : parseFloat(product.subtotal || 0),
            sub_total: hasApiLineAmounts ? String(apiLineSubtotal) : (product.sub_total !== undefined ? String(product.sub_total) : undefined),
            line_subtotal: hasApiLineAmounts ? apiLineSubtotal : parseFloat(product.line_subtotal || 0) || undefined,
            line_vat: Number.isFinite(apiLineVat) ? apiLineVat : undefined,
            line_tax: Number.isFinite(apiLineVat) ? apiLineVat : undefined,
            vat: Number.isFinite(apiLineVat) ? String(apiLineVat) : (product.vat !== undefined ? String(product.vat) : undefined),
            tax: Number.isFinite(apiLineVat) ? String(apiLineVat) : (product.tax !== undefined ? String(product.tax) : undefined),
            promo_unit_price:
                Number.isFinite(promoUnitPriceParsed) ? promoUnitPriceParsed : undefined,
            base_unit_price:
                Number.isFinite(baseUnitPriceParsed) ? baseUnitPriceParsed : undefined,
            tax_method: product.tax_method || "exclusive",
            promotion_applied: promotionAppliedFlag,
            useBackendCalculations: hasApiLineAmounts,
            availableBoxQty: parseFloat(product.availableBoxQty || 0),
            availablePieceQty: parseFloat(product.availablePieceQty || 0),
            size: product.size || "",
            inner_ean_number: product.inner_ean_number || "",
            outer_ean_number: product.outer_ean_number || "",
            innerEanNumber: product.inner_ean_number || "",
            outerEanNumber: product.outer_ean_number || "",
            discountedBoxPrice: parseFloat(product.discountedBoxPrice || 0),
            discountedSplitPrice: parseFloat(product.discountedSplitPrice || 0),
            productDiscountApplicable: parseInt(product.productDiscountApplicable || 0),
            accept_qty: product.accept_qty !== undefined ? String(product.accept_qty) : String(qty),
            allot_qty: product.allot_qty !== undefined ? String(product.allot_qty) : (product.accept_qty !== undefined ? String(product.accept_qty) : String(qty)),
            order_qty: String(orderQty),
            original_order_qty: String(orderQty),
            buyOrGet: normalizedPromoType || product.buyOrGet || null,
            isContractPriceApplied: product.isContractPriceApplied || product.is_contract_price_applied || "N",
            is_contract_price_applied: product.isContractPriceApplied || product.is_contract_price_applied || "N",
            standard_price: parseFloat(product.standard_price || 0),
            promotion_name: promotionDisplayName,
            promo_name: product.promo_name || promotionDisplayName,
            promotion_discount: promotionAppliedFlag
                ? [
                    product.promotion_discount,
                    product.promo_disc_amount,
                    product.promo_discount_amount,
                    product.line_discount,
                ].reduce((resolved, candidate) => {
                    if (resolved > 0.0001) return resolved;
                    const parsed = parseFloat(candidate);
                    return Number.isFinite(parsed) && parsed > 0.0001 ? parsed : resolved;
                }, 0)
                : 0,
            ...(Number.isFinite(promotionIdParsed) && promotionIdParsed > 0
                ? { promotion_id: promotionIdParsed, promo_id: promotionIdParsed }
                : {}),
            ...(resolvedSlabId ? { slab_id: resolvedSlabId, slabId: resolvedSlabId } : {}),
            ...(product.bundle_id != null && product.bundle_id !== "" ? { bundle_id: product.bundle_id } : {}),
            ...maxOrderQtyFields,
        };
    });
};

export const formatDateForAPI = (date = new Date()) => {
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

/** Offer-product swap via check-promotion `changed_get_products` (replaces change_get_product). */
export const checkPromotionWithGetProductChanges = async ({
    cartItems,
    customerId,
    channel,
    orderDate,
    orderTime = null,
    orderId = null,
    promotionId,
    removedProducts,
    newProducts,
    forEdit = false,
    previewOnly = false,
}) => {
    const cartDataToUse = {
        data: Array.isArray(cartItems) ? cartItems : [],
        success: true,
    };
    const payload = transformCartToPromotionPayload(
        cartDataToUse,
        customerId,
        orderDate,
        channel,
        orderTime,
        orderId
    );
    if (forEdit) {
        payload.for_edit = true;
    }
    if (previewOnly) {
        payload.preview_only = true;
    }
    const changed = buildChangedGetProductsForCheckPromotion(
        promotionId,
        removedProducts,
        newProducts
    );
    if (changed.length > 0) {
        payload.changed_get_products = changed;
    }
    return checkProductPromotion(payload);
};

